import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Clock, MapPin, ShieldCheck } from 'lucide-react';
import { useVenueBySlug } from '@/api/hooks/useVenues';
import type { VenueDetail, VenueSportSummary } from '@/api/hooks/useVenues';
import { useVenueSlugCapture } from '@/hooks/useVenueSlugCapture';
import VenueNavbar from '@/components/venue/VenueNavbar';
import type { AppError } from '@/api/client';

const DAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

type DayKey = (typeof DAY_ORDER)[number];

const DAY_LABELS: Record<DayKey, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

function todayKeyForTimezone(tz: string | null | undefined): DayKey {
  // Intl gives us the weekday in the venue's timezone — prevents showing
  // "Monday highlighted" to a Toronto user when the venue clock is already
  // into Tuesday (or vice versa).
  const tzSafe = tz || 'UTC';
  try {
    const name = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      timeZone: tzSafe,
    }).format(new Date());
    return name.toLowerCase() as DayKey;
  } catch {
    return new Date()
      .toLocaleDateString('en-US', { weekday: 'long' })
      .toLowerCase() as DayKey;
  }
}

function formatTime12h(hhmm: string | undefined): string {
  if (!hhmm) return '';
  // "23:59" → "11:59 PM"; "00:00" → "12:00 AM"
  const [hStr, mStr] = hhmm.split(':');
  const h = Number(hStr);
  const m = Number(mStr ?? '0');
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = ((h + 11) % 12) + 1;
  const mm = m.toString().padStart(2, '0');
  return `${hour12}:${mm} ${period}`;
}

export default function VenueDetailPage() {
  useVenueSlugCapture();
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error } = useVenueBySlug(slug);

  if (isLoading) return <VenueSkeleton />;
  if (error) {
    const appError = error as unknown as AppError | undefined;
    if (appError?.status === 404) return <VenueNotFound />;
    return <VenueLoadError />;
  }
  if (!data) return <VenueNotFound />;

  return <VenueDetail data={data} slug={slug ?? ''} />;
}

function VenueDetail({ data, slug }: { data: VenueDetail; slug: string }) {
  const bookHref = `/venue/${slug}/book`;
  const addressFull = [data.address, data.city, data.province]
    .filter((p) => p && p.length > 0)
    .join(', ');
  const mapsHref = `https://maps.google.com/?q=${encodeURIComponent(addressFull || data.name)}`;
  const today = todayKeyForTimezone(data.timezone);

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <VenueNavbar />

      {/* Hero — VenueNavbar is sticky/in-flow so no clearance padding needed;
           these paddings are purely breathing room. */}
      <section className="relative overflow-hidden pt-12 pb-10 md:pt-20 md:pb-16 bg-gradient-to-b from-[#110D09] via-[#1a1410] to-white text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(194,113,58,0.35),_transparent_55%)]"
        />
        <div className="relative mx-auto max-w-4xl px-4">
          <div className="text-sm font-medium uppercase tracking-[0.14em] text-[#C2713A]">
            Venue
          </div>
          <h1 className="mt-2 text-4xl md:text-5xl font-semibold tracking-[-0.02em] leading-[1.1]">
            {data.name}
          </h1>
          {addressFull && (
            <p className="mt-4 flex items-start gap-2 text-white/75">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#C2713A]" aria-hidden="true" />
              <span>{addressFull}</span>
            </p>
          )}
          {data.sports.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {data.sports.map((s) => (
                <span
                  key={s.sportCode}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-xs font-medium text-white/85"
                >
                  {s.icon && <span aria-hidden="true">{s.icon}</span>}
                  {s.displayName}
                </span>
              ))}
            </div>
          )}
          <div className="mt-8">
            <Link
              to={bookHref}
              className="group inline-flex items-center gap-2 rounded-md bg-[#C2713A] px-6 py-3 text-base font-medium text-white transition-all duration-200 hover:bg-[#a95f2f] hover:shadow-[0_0_28px_rgba(194,113,58,0.4)] hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2 focus-visible:ring-offset-[#110D09]"
            >
              Book Now
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-4xl px-4 pb-28 md:pb-20">
        {/* Sports */}
        <section aria-labelledby="sports-heading" className="pt-14 md:pt-20">
          <div className="mb-6 flex items-end justify-between">
            <h2
              id="sports-heading"
              className="text-2xl md:text-3xl font-semibold tracking-[-0.01em]"
            >
              What you can book
            </h2>
          </div>
          {data.sports.length === 0 ? (
            <p className="text-neutral-500">No sports currently available to book.</p>
          ) : (
            <div
              className={
                'grid gap-5 ' + (data.sports.length === 1 ? '' : 'md:grid-cols-2')
              }
            >
              {data.sports.map((sport) => (
                <SportCard key={sport.sportCode} sport={sport} slug={slug} />
              ))}
            </div>
          )}
        </section>

        {/* Operating hours */}
        <section aria-labelledby="hours-heading" className="mt-14 md:mt-20">
          <h2
            id="hours-heading"
            className="mb-5 flex items-center gap-2 text-2xl md:text-3xl font-semibold tracking-[-0.01em]"
          >
            <Clock className="h-6 w-6 text-[#C2713A]" aria-hidden="true" />
            Operating hours
          </h2>
          <ul className="divide-y divide-neutral-200 rounded-xl border border-neutral-200 bg-white">
            {DAY_ORDER.map((day) => {
              const dayHours = data.operatingHours?.[day];
              const isToday = day === today;
              return (
                <li
                  key={day}
                  className={
                    'flex items-center justify-between px-4 py-3 text-sm ' +
                    (isToday ? 'bg-[#FDF4EC]' : '')
                  }
                >
                  <div className="flex items-center gap-3">
                    <span className={isToday ? 'font-semibold' : 'font-medium'}>
                      {DAY_LABELS[day]}
                    </span>
                    {isToday && (
                      <span className="rounded-full bg-[#C2713A] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                        Today
                      </span>
                    )}
                  </div>
                  <span className="font-mono text-sm text-neutral-700">
                    {dayHours
                      ? `${formatTime12h(dayHours.open)} – ${formatTime12h(dayHours.close)}`
                      : 'Closed'}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Location */}
        {addressFull && (
          <section aria-labelledby="location-heading" className="mt-14 md:mt-20">
            <h2
              id="location-heading"
              className="mb-5 flex items-center gap-2 text-2xl md:text-3xl font-semibold tracking-[-0.01em]"
            >
              <MapPin className="h-6 w-6 text-[#C2713A]" aria-hidden="true" />
              Location
            </h2>
            <div className="rounded-xl border border-neutral-200 bg-white p-5">
              <p className="text-base text-neutral-900">{addressFull}</p>
              {data.timezone && (
                <p className="mt-1 text-sm text-neutral-500 font-mono">{data.timezone}</p>
              )}
              <a
                href={mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#C2713A] hover:text-[#a95f2f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] rounded-sm"
              >
                View on Google Maps
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
          </section>
        )}

        {/* Policies */}
        {data.cancellationSummary && (
          <section aria-labelledby="policies-heading" className="mt-14 md:mt-20">
            <h2
              id="policies-heading"
              className="mb-5 flex items-center gap-2 text-2xl md:text-3xl font-semibold tracking-[-0.01em]"
            >
              <ShieldCheck className="h-6 w-6 text-[#C2713A]" aria-hidden="true" />
              What to know
            </h2>
            <div className="rounded-xl border border-neutral-200 bg-white p-5">
              <p className="text-base text-neutral-800">{data.cancellationSummary}</p>
              <p className="mt-2 text-sm text-neutral-500">
                Full cancellation terms and session grace period are shown at checkout.
              </p>
            </div>
          </section>
        )}

        {/* Powered by */}
        <footer className="mt-16 border-t border-neutral-200 pt-8 text-center text-xs text-neutral-500">
          <p>
            Powered by{' '}
            <Link to="/" className="font-medium text-[#C2713A] hover:underline">
              RekPlay
            </Link>{' '}
            — the operating system for automated recreation facilities.
          </p>
        </footer>
      </main>

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-neutral-200 bg-white/95 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] backdrop-blur md:hidden">
        <Link
          to={bookHref}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-[#C2713A] px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-[#a95f2f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2"
        >
          Book Now
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

function SportCard({ sport, slug }: { sport: VenueSportSummary; slug: string }) {
  const href = `/venue/${slug}/book/${sport.sportCode}`;
  const durations =
    sport.allowedDurations && sport.allowedDurations.length > 0
      ? `${sport.allowedDurations[0]}–${sport.allowedDurations[sport.allowedDurations.length - 1]} min`
      : null;
  const accent = sport.color || '#C2713A';
  const unitLabel = sport.availableUnits != null
    ? `${sport.availableUnits} ${sport.availableUnits === 1 ? 'unit' : 'units'} available`
    : null;

  return (
    <Link
      to={href}
      className="group relative flex flex-col rounded-xl border border-neutral-200 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#C2713A]/40 hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.15)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C2713A] focus-visible:ring-offset-2"
      style={{ borderTop: `4px solid ${accent}` }}
    >
      <div className="mb-3 flex items-center gap-3">
        {sport.icon && <span className="text-3xl" aria-hidden="true">{sport.icon}</span>}
        <h3 className="text-lg font-semibold tracking-tight text-neutral-900">
          {sport.displayName}
        </h3>
      </div>
      {sport.description && (
        <p className="text-sm text-neutral-600 leading-[1.6]">{sport.description}</p>
      )}
      <dl className="mt-4 space-y-1 text-sm">
        {sport.startingPrice != null && (
          <div className="flex items-baseline justify-between">
            <dt className="text-neutral-500">Starting at</dt>
            <dd className="font-mono text-neutral-900">
              {sport.currency ? `${sport.currency} ` : '$'}
              {Number(sport.startingPrice).toFixed(2)}
              <span className="ml-1 text-xs text-neutral-500">/ hr</span>
            </dd>
          </div>
        )}
        {unitLabel && (
          <div className="flex items-baseline justify-between">
            <dt className="text-neutral-500">Availability</dt>
            <dd className="text-neutral-900">{unitLabel}</dd>
          </div>
        )}
        {durations && (
          <div className="flex items-baseline justify-between">
            <dt className="text-neutral-500">Session length</dt>
            <dd className="font-mono text-neutral-900">{durations}</dd>
          </div>
        )}
      </dl>
      <div
        className="mt-5 inline-flex items-center gap-2 text-sm font-semibold"
        style={{ color: accent }}
      >
        Book {sport.displayName}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

function VenueSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <VenueNavbar />
      <section className="pt-12 pb-10 md:pt-20 md:pb-16 bg-[#110D09]">
        <div className="mx-auto max-w-4xl px-4 animate-pulse">
          <div className="h-4 w-24 rounded bg-white/10" />
          <div className="mt-3 h-10 w-3/4 rounded bg-white/10" />
          <div className="mt-4 h-4 w-1/2 rounded bg-white/5" />
          <div className="mt-8 h-12 w-40 rounded bg-white/10" />
        </div>
      </section>
      <main className="mx-auto max-w-4xl px-4 pb-20 pt-14">
        <div className="h-7 w-48 rounded bg-neutral-200 animate-pulse" />
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-48 rounded-xl border border-neutral-200 bg-neutral-50 animate-pulse" />
          ))}
        </div>
      </main>
    </div>
  );
}

function VenueNotFound() {
  return (
    <div className="min-h-screen bg-white">
      <VenueNavbar />
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-4 text-center">
        <h1 className="text-3xl font-semibold tracking-tight">Venue not found</h1>
        <p className="mt-3 text-neutral-600">
          We couldn't find a venue with that name. It may have moved or is no longer
          listed.
        </p>
        <Link
          to="/venues"
          className="mt-8 inline-flex items-center gap-2 rounded-md bg-[#C2713A] px-6 py-3 text-base font-medium text-white transition-colors hover:bg-[#a95f2f]"
        >
          Browse all venues
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

function VenueLoadError() {
  return (
    <div className="min-h-screen bg-white">
      <VenueNavbar />
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-4 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Something went wrong</h1>
        <p className="mt-3 text-neutral-600">
          We couldn't load this venue right now. Please try again in a moment.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-8 inline-flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-5 py-3 text-base font-medium text-neutral-900 transition-colors hover:bg-neutral-50"
        >
          Retry
        </button>
      </div>
    </div>
  );
}
